import type { TestDriveBookingRecord } from '../../../api/services/lupp/test-drive/dto.test-drive';
import { formatVehicleModelName, getDateLocale } from '../../../utils/tools';
import type { BookingFilters } from './hooks/useBookingSearchTable';

/**
 * Status color/label helpers for the agency-wide test-drive booking search table, keyed off
 * `testDriveStatus` (the booking lifecycle status) — the same field `BookingSearchRequest.status`
 * filters by. Distinct from `hostAssignmentStatus`, which tracks host-assignment workflow state.
 */
export const getBookingStatusBadgeColor = (status?: string) => {
  switch ((status ?? '').toLowerCase()) {
    case 'requested':
      return 'yellow';
    case 'scheduled':
      return 'blue';
    case 'completed':
      return 'teal';
    case 'cancelled':
      return 'gray';
    case 'no_show':
    case 'no show':
    case 'noshow':
      return 'red';
    default:
      return 'gray';
  }
};

type BookingStatusKey = 'requested' | 'scheduled' | 'completed' | 'cancelled' | 'no_show';

const BOOKING_STATUS_DEFAULTS: Record<BookingStatusKey, string> = {
  requested: 'Requested',
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No Show',
};

const BOOKING_STATUS_VALUES: { key: BookingStatusKey; value: string }[] = [
  { key: 'scheduled', value: 'Scheduled' },
  { key: 'completed', value: 'Completed' },
  { key: 'cancelled', value: 'Cancelled' },
  { key: 'no_show', value: 'No Show' },
];

const isBookingStatusKey = (key: string): key is BookingStatusKey => key in BOOKING_STATUS_DEFAULTS;

/** Contentful label key for a status, e.g. `status.scheduled`. English default is used when unset. */
export const getBookingStatusLabel = (status?: string, labels?: Record<string, string>) => {
  const key = (status ?? '').toLowerCase().replace(/[\s-]+/g, '_');
  if (isBookingStatusKey(key)) {
    return labels?.[`status.${key}`] || BOOKING_STATUS_DEFAULTS[key];
  }
  return status ?? '-';
};

/** Status dropdown options: label from Contentful (`status.*`), value is the raw status the API filters by. */
export const getBookingStatusOptions = (
  labels?: Record<string, string>
): { label: string; value: string }[] =>
  BOOKING_STATUS_VALUES.map(({ key, value }) => ({
    label: labels?.[`status.${key}`] || BOOKING_STATUS_DEFAULTS[key],
    value,
  }));

/** Short, human-friendly Test Drive ID for display; the full ID is kept in the tooltip/title. */
export const formatTestDriveId = (id?: string) => (id ? id.slice(0, 8).toUpperCase() : '-');

export const getTestDriveTypeLabel = (type?: string, labels: Record<string, string> = {}) => {
  switch ((type ?? '').toUpperCase()) {
    case 'STANDARD':
      return labels['type.standard'] || 'Standard';
    case 'EXTENDED':
      return labels['type.extended'] || 'Extended';
    default:
      return type || '-';
  }
};

export const getProspectDisplayName = (record: TestDriveBookingRecord) =>
  record.prospectFullName ||
  [record.prospect?.prospectFirstName, record.prospect?.prospectLastName]
    .filter(Boolean)
    .join(' ') ||
  '';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Normalises a date query param to the ISO-8601 instant the search API expects. A bare
 * `yyyy-MM-dd` becomes the start or end of that local day; a full ISO value passes through.
 * Returns undefined for anything unparseable so a malformed link can't break the search.
 */
export const toBookingDateInstant = (
  value: string | null,
  boundary: 'start' | 'end'
): string | undefined => {
  if (!value) return undefined;
  const dateOnly = DATE_ONLY.exec(value);
  if (!dateOnly) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }
  const [year, month, day] = dateOnly.slice(1).map(Number);
  const [hours, minutes, seconds, ms] = boundary === 'start' ? [0, 0, 0, 0] : [23, 59, 59, 999];
  const date = new Date(year, month - 1, day, hours, minutes, seconds, ms);
  // Date silently rolls over out-of-range parts (e.g. month 13), so reject anything that moved.
  const isRealDate =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isRealDate ? date.toISOString() : undefined;
};

const DATE_FILTER_PARAMS = [
  { key: 'appointmentStartDate', boundary: 'start' },
  { key: 'appointmentEndDate', boundary: 'end' },
  { key: 'appointmentEndFrom', boundary: 'start' },
  { key: 'appointmentEndTo', boundary: 'end' },
] as const;

const TEXT_FILTER_PARAMS = [
  'status',
  'vehicleModel',
  'vin',
  'testDriveType',
  'locationName',
  'testDriveId',
] as const;

export type BookingDateType = 'start' | 'end';

/** Legacy links with both ranges use appointment start, so no end-time filter stays hidden. */
export const getBookingDateType = (filters: BookingFilters): BookingDateType =>
  !filters.appointmentStartDate &&
  !filters.appointmentEndDate &&
  (filters.appointmentEndFrom || filters.appointmentEndTo)
    ? 'end'
    : 'start';

/** Moves the current range to the selected date type, removing the inactive API bounds. */
export const withBookingDateType = (
  filters: BookingFilters,
  type: BookingDateType
): BookingFilters => {
  const {
    appointmentStartDate,
    appointmentEndDate,
    appointmentEndFrom,
    appointmentEndTo,
    ...rest
  } = filters;
  const currentType = getBookingDateType(filters);
  const from = currentType === 'start' ? appointmentStartDate : appointmentEndFrom;
  const to = currentType === 'start' ? appointmentEndDate : appointmentEndTo;
  const next: BookingFilters = rest;
  if (from) next[type === 'start' ? 'appointmentStartDate' : 'appointmentEndFrom'] = from;
  if (to) next[type === 'start' ? 'appointmentEndDate' : 'appointmentEndTo'] = to;
  return next;
};

/** Builds the initial filter-panel state from URL query params so a filtered view can be linked. */
export const parseBookingFiltersFromSearchParams = (params: URLSearchParams): BookingFilters => {
  const filters: BookingFilters = {};
  DATE_FILTER_PARAMS.forEach(({ key, boundary }) => {
    const instant = toBookingDateInstant(params.get(key), boundary);
    if (instant) filters[key] = instant;
  });
  TEXT_FILTER_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) filters[key] = value;
  });
  return withBookingDateType(filters, getBookingDateType(filters));
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Keeps the local filter day intact in the DatePicker, whose calendar uses UTC dates. */
export const getBookingDatePickerValue = (value?: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

/** Inverse of `toBookingDateInstant`: a whole-day boundary becomes `yyyy-MM-dd`, anything else stays a full ISO instant. */
const fromInstant = (value: string, boundary: 'start' | 'end'): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const isBoundary =
    boundary === 'start'
      ? date.getHours() === 0 &&
        date.getMinutes() === 0 &&
        date.getSeconds() === 0 &&
        date.getMilliseconds() === 0
      : date.getHours() === 23 &&
        date.getMinutes() === 59 &&
        date.getSeconds() === 59 &&
        date.getMilliseconds() === 999;
  return isBoundary
    ? `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
    : value;
};

/**
 * Writes the active filters (and prospect scope) back onto the URL query so the current view can be
 * shared or refreshed. Params this page doesn't manage are left untouched.
 */
export const applyBookingFiltersToSearchParams = (
  current: URLSearchParams,
  filters: BookingFilters,
  prospectId?: string
): URLSearchParams => {
  const next = new URLSearchParams(current);
  ['prospectId', ...TEXT_FILTER_PARAMS, ...DATE_FILTER_PARAMS.map(({ key }) => key)].forEach(
    (key) => next.delete(key)
  );
  if (prospectId) next.set('prospectId', prospectId);
  TEXT_FILTER_PARAMS.forEach((key) => {
    const value = filters[key];
    if (value) next.set(key, value);
  });
  DATE_FILTER_PARAMS.forEach(({ key, boundary }) => {
    const value = filters[key];
    if (value) next.set(key, fromInstant(value, boundary));
  });
  return next;
};

/** Filter panel field labels, keyed by filter. */
const BOOKING_FILTER_LABELS: Record<keyof BookingFilters, [labelKey: string, fallback: string]> = {
  testDriveId: ['table.testDriveId', 'Test Drive ID'],
  status: ['table.status', 'Status'],
  vehicleModel: ['table.vehicleModel', 'Vehicle model'],
  vin: ['table.vin', 'VIN'],
  testDriveType: ['table.testDriveType', 'Test Drive Type'],
  locationName: ['table.location', 'Location'],
  appointmentStartDate: ['table.startDateFrom', 'Appointment Start From'],
  appointmentEndDate: ['table.startDateTo', 'Appointment Start To'],
  appointmentEndFrom: ['table.endDateFrom', 'Appointment End From'],
  appointmentEndTo: ['table.endDateTo', 'Appointment End To'],
};

export const getBookingFilterLabel = (
  key: keyof BookingFilters,
  labels?: Record<string, string>
) => {
  const [labelKey, fallback] = BOOKING_FILTER_LABELS[key];
  return labels?.[labelKey] ?? fallback;
};

const DATE_FILTER_KEYS = new Set<keyof BookingFilters>(DATE_FILTER_PARAMS.map(({ key }) => key));

/** How an applied filter value reads in its pill, e.g. a status label or a local date. */
export const getBookingFilterDisplayValue = (
  key: keyof BookingFilters,
  value: string,
  labels?: Record<string, string>,
  locale = 'en-US'
): string => {
  if (key === 'status') return getBookingStatusLabel(value, labels);
  if (key === 'testDriveType') return getTestDriveTypeLabel(value, labels);
  if (key === 'vehicleModel') return formatVehicleModelName(value);
  if (DATE_FILTER_KEYS.has(key)) {
    return new Date(value).toLocaleDateString(getDateLocale(locale), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }
  return value;
};
